/**
 * Human-readable labels for audit log action and entity type values.
 *
 * Keep in sync with internal/auth/actions.go.
 * When a new Action or EntityType constant lands there, add the label here.
 * ACTION_OPTIONS / ENTITY_TYPE_OPTIONS are derived automatically.
 */

export const ACTION_LABELS: Record<string, string> = {
  // Auth
  login_success: "Login",
  login_failure: "Failed login",
  logout: "Logout",
  password_change: "Password change",

  // Users
  user_created: "User created",
  user_updated: "User updated",
  user_deleted: "User deleted",

  // Senders
  sender_created: "Sender created",
  sender_updated: "Sender updated",
  sender_deleted: "Sender deleted",

  // Templates
  template_created: "Template created",
  template_updated: "Template updated",
  template_deleted: "Template deleted",
  template_published: "Template published",

  // API keys
  api_key_created: "API key created",
  api_key_revoked: "API key revoked",
  api_key_deleted: "API key deleted",

  // Worker tokens
  worker_token_created: "Worker token created",
  worker_token_revoked: "Worker token revoked",
  worker_token_deleted: "Worker token deleted",

  // Messages & batches
  message_retried: "Message retried",
  message_cancelled: "Message cancelled",
  message_redelivered: "Message redelivered",
  message_composed: "Message sent manually",
  message_enqueued: "Message sent via API",
  batch_created: "Batch created",
  batch_cancelled: "Batch cancelled",

  // Suppressions
  suppression_created: "Suppression created",
  suppression_deleted: "Suppression removed",

  // Webhooks
  webhook_created: "Webhook created",
  webhook_updated: "Webhook updated",
  webhook_deleted: "Webhook deleted",

  // Maintenance
  audit_pruned: "Audit log pruned",
  messages_pruned: "Old messages pruned",
};

export const ENTITY_TYPE_LABELS: Record<string, string> = {
  user: "User",
  sender: "Sender",
  template: "Template",
  api_key: "API key",
  worker_token: "Worker token",
  message: "Message",
  batch: "Batch",
  suppression: "Suppression",
  webhook: "Webhook",
};

export const ACTION_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All" },
  ...Object.entries(ACTION_LABELS)
    .sort((a, b) => a[1].localeCompare(b[1], "en"))
    .map(([value, label]) => ({ value, label })),
];

export const ENTITY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All" },
  ...Object.entries(ENTITY_TYPE_LABELS)
    .sort((a, b) => a[1].localeCompare(b[1], "en"))
    .map(([value, label]) => ({ value, label })),
];

export function labelAction(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function labelEntityType(entityType: string): string {
  return ENTITY_TYPE_LABELS[entityType] ?? entityType;
}
